/**
 * Las repeticiones de una campaña (28/10/2026) en las pantallas de siempre:
 * - `ChipSeRepite`: en la lista, «Se repite» (o «Repetición pausada») junto al nombre de la madre.
 * - `RenglonRepeticion`: en la lista y el detalle de una repetición, «Repetición N de <madre>» con el link y, si quedó
 *   en borrador, por qué (lo que el programador dejó en `repeticionDe.motivos`).
 * - `TarjetaRepeticion`: en el detalle de la madre, «Se repite cada… · próxima: …», cómo quedó la última, «Pausar»,
 *   «Reanudar», «Terminar» y «Cambiar» (del equipo), y la lista de sus repeticiones con su estado. Si una lista de
 *   exclusión de la serie se borró (29/10/2026), el aviso y «Sacar la lista borrada de la serie» (`AvisoListaBorrada`).
 * El equipo y el cliente ven la regla y las repeticiones (es leer lo de su comercio); los botones y los motivos de una que
 * quedó en borrador, sólo el equipo: están escritos para quien la arregla («cargá saldo», «revisala y confirmala»). Al
 * cliente se le dice que la está revisando el equipo de Deenex.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Loader2, Pause, Play, Repeat, Square } from "lucide-react";
import { oficialApi } from "../api";
import type { CampanaDetalle, CampanaResumen, ResumenRepeticion } from "../types";
import { leerError, leerRespuesta, textoError } from "../logic/respuesta";
import { ESTADO_REPETICION, fechaDeLaSerie, rutaRepetir, textoDecision } from "../logic/repeticion";
import { rutaCampana } from "../logic/seguimiento";
import { ZONA_MARCA, paisDeZona } from "../logic/zonas";
import { useEsEquipoDeenex } from "../../campana-nueva/hooks/useEsEquipoDeenex";
import { ChipEstadoCampanaOficial } from "./ChipEstadoCampanaOficial";
import { Tarjeta } from "./Tarjeta";

const enZona = (zona: string) => (zona && zona !== ZONA_MARCA ? ` (hora de ${paisDeZona(zona)})` : "");
const TEXTO_BORRADOR_CLIENTE = "La está revisando el equipo de Deenex antes de que salga.";

export function ChipSeRepite({ repeticion }: { repeticion?: ResumenRepeticion }) {
  if (!repeticion || repeticion.estado === "terminada") return null;
  return (
    <span title={repeticion.texto} data-testid="chip-se-repite"
      className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 inline-flex items-center gap-1">
      <Repeat size={10} aria-hidden="true" /> {ESTADO_REPETICION[repeticion.estado]}
    </span>
  );
}

/**
 * «Repetición 3 de Campeones · sale el jue 12/11 19:00», y si quedó en borrador, por qué. Un borrador cuya fecha ya pasó
 * «le tocaba el …» (29/10/2026: antes decía «sale el» con una fecha que ya había pasado).
 */
export function RenglonRepeticion({ campana }: { campana: Pick<CampanaResumen, "repeticionDe" | "estado"> }) {
  const equipo = useEsEquipoDeenex();
  const de = campana.repeticionDe;
  if (!de) return null;
  const enBorrador = campana.estado === "borrador" && de.decision === "borrador" && de.motivos.length > 0;
  const vencida = campana.estado === "borrador" && new Date(de.programadaPara).getTime() < Date.now();
  const verbo = vencida ? "le tocaba" : campana.estado === "borrador" || campana.estado === "programada" ? "sale" : "salió";
  return (
    <div className="mt-1 space-y-1 text-xs text-slate-500">
      <p className="flex items-center gap-1.5">
        <Repeat size={12} className="flex-shrink-0 text-violet-500" aria-hidden="true" />
        <span>
          Repetición {de.n} de <Link to={rutaCampana(de.campanaId)} className="font-semibold text-violet-600 hover:underline">{de.nombre}</Link>
          <span className="text-slate-400"> · {verbo} el {fechaDeLaSerie(de.programadaPara, de.zona || ZONA_MARCA)}{enZona(de.zona || ZONA_MARCA)}</span>
        </span>
      </p>
      {enBorrador && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800" role="note" data-testid="motivos-repeticion">
          <p className="font-semibold">Quedó en borrador: no sale hasta que alguien la confirme.</p>
          {equipo
            ? <ul className="mt-1 list-disc pl-4 space-y-0.5">{de.motivos.map((m) => <li key={m}>{m}</li>)}</ul>
            : <p className="mt-0.5">{TEXTO_BORRADOR_CLIENTE}</p>}
        </div>
      )}
    </div>
  );
}

type Accion = "pausar" | "reanudar" | "terminar";

/**
 * Una lista de exclusión de la serie que se borró (29/10/2026): frena el chequeo de cada repetición (es lo seguro: si no,
 * a «Empleados» le llegaría la promoción por error) y cada una queda en borrador. El equipo la saca de la original, de la
 * que se copia cada repetición, con una confirmación en línea (no una ventana encima); las que ya se armaron se arreglan
 * en su asistente. Sólo el equipo: es un motivo para arreglar, como los de una repetición en borrador.
 */
function AvisoListaBorrada({ campanaId, lista, onActualizada }: { campanaId: string; lista: { id: string; nombre: string }; onActualizada: (c: CampanaDetalle) => void }) {
  const [confirmar, setConfirmar] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const cual = lista.nombre ? `La lista «${lista.nombre}»` : "Una lista de exclusión de la serie";

  const sacar = async () => {
    setBusy(true);
    setError("");
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.sacarListaBorradaDeLaSerie(campanaId, lista.id));
      if (!l.ok) { setError(textoError(l)); return; }
      setConfirmar(false);
      onActualizada(l.data);
    } catch (e) {
      // 409 ESTADO_INVALIDO: otro la sacó mientras tanto
      const x = leerError(e);
      setError(x.code === "ESTADO_INVALIDO" ? `${x.error || "La serie cambió."} ` : textoError(x));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="note" aria-label="Lista de exclusión borrada" data-testid="lista-borrada">
      <p className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span>{cual} se borró: las repeticiones quedan en borrador.</span>
      </p>
      {!confirmar ? (
        <button type="button" onClick={() => setConfirmar(true)}
          className="h-8 px-3 rounded-lg border border-amber-300 bg-white text-xs font-semibold text-amber-800 hover:bg-amber-100 inline-flex items-center gap-1.5">
          Sacar la lista borrada de la serie
        </button>
      ) : (
        <div role="group" aria-label="Confirmar sacar la lista borrada de la serie" className="flex items-center gap-2 flex-wrap">
          <span>¿Sacarla de la serie? Las próximas repeticiones salen sin esa exclusión; las que ya se armaron se arreglan en su asistente.</span>
          <button type="button" onClick={() => void sacar()} disabled={busy} className="font-semibold text-amber-900 hover:underline disabled:opacity-60">
            {busy ? "Sacando…" : "Sí, sacarla"}
          </button>
          <button type="button" onClick={() => setConfirmar(false)} disabled={busy} className="font-semibold text-slate-500 hover:underline disabled:opacity-60">
            No, dejarla
          </button>
        </div>
      )}
      {error && <p role="alert" className="text-red-700">{error}</p>}
    </div>
  );
}

export function TarjetaRepeticion({ campana, equipo, onActualizada }: { campana: CampanaDetalle; equipo: boolean; onActualizada: (c: CampanaDetalle) => void }) {
  const r = campana.repeticion;
  const [busy, setBusy] = useState<Accion | "">("");
  const [confirmarTerminar, setConfirmarTerminar] = useState(false);
  const [error, setError] = useState("");
  if (!r) return null;

  const accion = async (a: Accion) => {
    setBusy(a);
    setError("");
    try {
      const pedido = a === "pausar" ? oficialApi.pausarRepeticion(campana.id) : a === "reanudar" ? oficialApi.reanudarRepeticion(campana.id) : oficialApi.terminarRepeticion(campana.id);
      const l = leerRespuesta<CampanaDetalle>(await pedido);
      if (!l.ok) { setError(textoError(l)); return; }
      setConfirmarTerminar(false);
      onActualizada(l.data);
    } catch (e) {
      // 409 ESTADO_INVALIDO: otro la cambió mientras tanto
      const x = leerError(e);
      setError(x.code === "ESTADO_INVALIDO" ? `${x.error || "La repetición cambió."} Recargá la página.` : textoError(x));
    } finally {
      setBusy("");
    }
  };

  const u = r.ultima;
  const ultimaEnBorrador = !!u && !u.salio && u.estado === "borrador";
  // las listas de exclusión de la serie que se borraron: cada repetición frena por ellas (sólo mientras se repite)
  const borradas = equipo && r.estado !== "terminada" ? (campana.audiencia?.excluir?.listas || []).filter((l) => l.borrada) : [];
  const filas = campana.repeticiones || [];
  const boton = "h-8 px-3 rounded-lg border text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50";

  return (
    <Tarjeta>
      <div className="space-y-3" data-testid="tarjeta-repeticion">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 space-y-1">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <Repeat size={15} className="text-violet-600" aria-hidden="true" /> {r.texto}
            </p>
            <p className="text-xs text-slate-500">
              {r.estado === "activa" && r.proxima && <span className="text-indigo-700 font-medium">Próxima: {fechaDeLaSerie(r.proxima, r.zona)}{enZona(r.zona)}</span>}
              {r.estado === "pausada" && <span className="text-amber-700 font-medium">Pausada: no se arma ninguna hasta reanudarla</span>}
              {r.estado === "terminada" && (
                <span className="font-medium" data-testid="repeticion-terminada">
                  Terminada: {r.motivoFin ? `${r.motivoFin} No se arma ninguna más; las que no habían salido se cancelaron.` : "no se arma ninguna más"}
                </span>
              )}
              {r.estado !== "terminada" && (
                <span> · {r.saleSola ? "Sale sola si está todo en orden" : "Cada repetición queda en borrador para confirmarla"}</span>
              )}
            </p>
          </div>
          {equipo && (
            <div className="flex gap-2 flex-wrap items-start">
              {r.estado === "activa" && (
                <button type="button" onClick={() => void accion("pausar")} disabled={!!busy} className={`${boton} border-slate-200 bg-white text-slate-700 hover:bg-slate-50`}>
                  {busy === "pausar" ? <Loader2 size={13} className="animate-spin" /> : <Pause size={13} />} Pausar
                </button>
              )}
              {r.estado === "pausada" && (
                <button type="button" onClick={() => void accion("reanudar")} disabled={!!busy} className={`${boton} border-violet-600 bg-violet-600 text-white hover:bg-violet-700`}>
                  {busy === "reanudar" ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Reanudar
                </button>
              )}
              {/* una original cancelada no se vuelve a repetir (`puedeRepetir` del backend) */}
              {(r.estado !== "terminada" || campana.puedeRepetir !== false) && (
                <Link to={rutaRepetir(campana.id)} className={`${boton} border-violet-200 bg-white text-violet-700 hover:bg-violet-50`}>
                  <Repeat size={13} /> {r.estado === "terminada" ? "Volver a repetir" : "Cambiar"}
                </Link>
              )}
              {r.estado !== "terminada" && !confirmarTerminar && (
                <button type="button" onClick={() => setConfirmarTerminar(true)} disabled={!!busy} className={`${boton} border-red-200 bg-white text-red-700 hover:bg-red-50`}>
                  <Square size={12} /> Terminar
                </button>
              )}
            </div>
          )}
        </div>

        {equipo && confirmarTerminar && (
          <div role="group" aria-label="Confirmar terminar la repetición" className="flex items-center gap-2 flex-wrap text-xs">
            <span className="text-slate-600">¿Terminar la repetición? No se arma ninguna más; las que ya se armaron siguen como cualquier campaña.</span>
            <button type="button" onClick={() => void accion("terminar")} disabled={!!busy} className="font-semibold text-red-700 hover:underline disabled:opacity-60">
              {busy === "terminar" ? "Terminando…" : "Sí, terminar"}
            </button>
            <button type="button" onClick={() => setConfirmarTerminar(false)} disabled={!!busy} className="font-semibold text-slate-500 hover:underline disabled:opacity-60">
              No, dejarla
            </button>
          </div>
        )}
        {error && <p role="alert" className="text-xs text-red-700">{error}</p>}

        {borradas.map((l) => <AvisoListaBorrada key={l.id} campanaId={campana.id} lista={l} onActualizada={onActualizada} />)}

        {ultimaEnBorrador && u && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800" role="note">
            <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
            <div>
              <p>
                La repetición del {fechaDeLaSerie(u.programadaPara, r.zona)} quedó en borrador:{" "}
                <Link to={rutaCampana(u.campanaId)} className="font-semibold underline">{u.nombre}</Link>.
              </p>
              {!equipo ? <p className="mt-0.5">{TEXTO_BORRADOR_CLIENTE}</p>
                : u.motivos.length > 0 && <ul className="mt-1 list-disc pl-4 space-y-0.5">{u.motivos.map((m) => <li key={m}>{m}</li>)}</ul>}
            </div>
          </div>
        )}

        {filas.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Sus repeticiones</p>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100" aria-label="Repeticiones de la campaña">
              {filas.map((f) => (
                <li key={f.id} className="flex items-center justify-between gap-3 flex-wrap px-3 py-2 text-xs">
                  <span className="min-w-0">
                    <span className="text-slate-400">{f.n}. </span>
                    <Link to={rutaCampana(f.id)} className="font-semibold text-violet-600 hover:underline">{f.nombre}</Link>
                    <span className="text-slate-500"> · {fechaDeLaSerie(f.programadaPara, r.zona)}</span>
                  </span>
                  <span className="inline-flex items-center gap-2">
                    {textoDecision(f.decision, f.estado) && (
                      <span className={f.estado === "borrador" && f.decision === "borrador" ? "text-amber-700 font-medium" : "text-slate-500"}>{textoDecision(f.decision, f.estado)}</span>
                    )}
                    <ChipEstadoCampanaOficial estado={f.estado} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Tarjeta>
  );
}
